/**
 * Authentication service using AWS Amplify and Cognito
 */
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  signInWithRedirect,
  AuthUser,
} from '@aws-amplify/auth';

// Configure Amplify with Cognito settings
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;

if (userPoolId && userPoolClientId) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: {
          oauth: {
            domain: cognitoDomain,
            scopes: ['email', 'openid', 'profile'],
            redirectSignIn: [window.location.origin + '/callback'],
            redirectSignOut: [window.location.origin],
            responseType: 'code',
          },
        },
      },
    },
  });
}

export interface User {
  userId: string;
  username: string;
  email?: string;
}

/**
 * Get the current authenticated user
 */
export async function getAuthenticatedUser(): Promise<User | null> {
  try {
    const user: AuthUser = await getCurrentUser();
    const session = await fetchAuthSession();
    const email =
      session.tokens?.idToken?.payload?.email as string | undefined;
    return {
      userId: user.userId,
      username: user.username,
      email: email || user.signInDetails?.loginId,
    };
  } catch {
    return null;
  }
}

/**
 * Get the current auth session token
 */
export async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

/**
 * Sign in with username and password (for testing/development)
 */
export async function signInWithCredentials(
  username: string,
  password: string
): Promise<User> {
  const result = await signIn({ username, password });

  if (result.isSignedIn) {
    const user = await getAuthenticatedUser();
    if (user) return user;
  }

  throw new Error('Sign in failed');
}

/**
 * Sign in using Cognito Hosted UI (OAuth redirect)
 */
export async function signInWithHostedUI(): Promise<void> {
  await signInWithRedirect();
}

/**
 * Sign out the current user
 */
export async function signOutUser(): Promise<void> {
  await signOut();
}

/**
 * Handle OAuth callback - extract tokens from URL
 */
export async function handleAuthCallback(): Promise<User | null> {
  // Amplify automatically handles the callback when configured
  // Just check if we're now authenticated
  return getAuthenticatedUser();
}
